import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import usePageTitle from "../hooks/usePageTitle";

export default function Login() {
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remembered, setRemembered] = useState(null); // { id, name, image, role }

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('user') || 'null');
      const savedId = localStorage.getItem('sliitid') || (saved && saved.SLIITID) || '';
      if (savedId) {
        setRemembered({
          id: savedId,
          name: saved?.name || 'Leo Member',
          image: saved?.image || '/user.png',
          role: saved?.role || ''
        });
        setId(savedId);
      }
    } catch {}
  }, []);

  const avatarSrc = useMemo(() => (remembered?.image ? remembered.image : '/user.png'), [remembered]);

  const switchToAnotherUser = () => {
    setRemembered(null);
    setPassword('');
    setError('');
    setId('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const submitId = remembered ? remembered.id : id;
    if (!submitId || !password) {
      setError('SLIIT ID and Password are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('https://dev.sliitleo.org/Backend/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ SLIITID: submitId, AccountPassword: password })
      });
      let data = {};
      try { data = await res.json(); } catch {}
      if (data.success) {
        const userData = {
          SLIITID: submitId,
          name: data.full_name ?? (remembered?.name || 'Leo Member'),
          address: data.address ?? '',
          contactno: data.contactno ?? '',
          role: data.role ?? '',
          image: data.image ?? remembered?.image ?? '/user.png',
        };
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('sliitid', submitId);
        if ((userData.role || '').toLowerCase() === 'banned') {
          window.location.href = '/restricted';
        } else {
          window.location.href = '/home';
        }
      } else {
        setError(data.error || data.message || 'Login failed');
      }
    } catch {
      setError('Server error');
    } finally {
      setLoading(false);
    }
  };

  const Dot = ({ className = '' }) => (
    <span className={`absolute w-3 h-3 rounded-full ring-2 ring-black/40 ${className}`} />
  );

  // Animation presets
  const slideIn = {
    initial: { x: -40, opacity: 0, scale: 0.98 },
    animate: { x: 0, opacity: 1, scale: 1 },
    transition: { duration: 0.35, ease: 'easeOut' }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-black text-white">
      {/* Background Image */}
      <img
        src="/8.png"
        alt=""
        className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover opacity-20"
      />

      {/* Full-page SVG WAVE */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        <defs>
          <linearGradient id="bgWave" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.18" />
            <stop offset="50%" stopColor="#6366f1" stopOpacity="0.20" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.18" />
          </linearGradient>
          <filter id="bgGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="22" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect width="1440" height="900" fill="transparent" />
        <path d="M0,720 C220,700 320,780 540,740 C760,700 880,560 1100,600 C1260,630 1350,700 1440,680 L1440,900 L0,900 Z"
              fill="url(#bgWave)" opacity="0.45" filter="url(#bgGlow)" />
        <path d="M0,690 C240,660 360,750 560,720 C780,690 920,580 1140,620 C1290,650 1380,710 1440,690 L1440,900 L0,900 Z"
              fill="url(#bgWave)" opacity="0.30" />
        <path d="M0,760 C240,740 380,820 580,790 C780,760 940,620 1160,660 C1310,690 1400,760 1440,760 L1440,900 L0,900 Z"
              fill="url(#bgWave)" opacity="0.22" />
        <path fill="none" stroke="#93c5fd" strokeOpacity="0.55" strokeWidth="1.6">
          <animate attributeName="d" dur="7s" repeatCount="indefinite"
            values="
              M0,640 C200,600 400,680 600,640 C800,600 1000,680 1200,640 C1320,620 1380,630 1440,640;
              M0,630 C200,650 400,600 600,650 C800,600 1000,640 1200,650 C1320,640 1380,650 1440,630;
              M0,640 C200,600 400,680 600,640 C800,600 1000,680 1200,640 C1320,620 1380,630 1440,640
            " />
        </path>
      </svg>

      {/* LEFT dark gradient overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="hidden lg:block absolute inset-y-0 left-0 w-[56%] bg-gradient-to-br from-[#0b1020]/85 via-[#0b1020]/65 to-transparent" />
        <div className="lg:hidden absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-[#0b1020]/85 via-[#0b1020]/40 to-transparent" />
      </div>

      {/* CONTENT */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* LEFT: auth block (lifted up slightly) */}
        <div className="flex items-center justify-center px-4 pt-8 pb-10 lg:pt-0">
          <motion.div
            {...slideIn}
            className="w-full max-w-md -mt-4 lg:-mt-10"  /* 👈 lifted up */
          >
            <div className="bg-[#0b1020]/80 backdrop-blur-xl rounded-2xl p-8 shadow-[0_20px_80px_-40px_rgba(0,0,0,.9)] ring-1 ring-white/10">
              {/* Header */}
              <div className="flex items-center gap-2 mb-6">
                <img src="/club.png" alt="Logo" className="h-8" />
                <span className="bg-white/10 px-2 py-1 rounded text-xs font-medium">
                 Club Portal 4.0
                </span>
              </div>

              <h1 className="text-2xl font-extrabold tracking-tight">
                {remembered ? 'Welcome back' : 'Sign in to your account'}
              </h1>
              {!remembered && (
                <p className="text-sm text-white/60 mt-1">
                  Not a member yet?{' '}
                  <a
                    href="https://members.sliitleo.org/"
                    className="text-blue-300 hover:text-blue-200 underline underline-offset-2"
                  >
                    Register now
                  </a>
                </p>
              )}

              <form onSubmit={handleLogin} className="mt-7 space-y-5">
                {/* Remembered identity strip OR ID input */}
                {remembered ? (
                  <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 ring-1 ring-white/10 relative">
                    <div className="relative">
                      <img
                        src={avatarSrc}
                        alt={remembered.name}
                        className="h-12 w-12 rounded-full object-cover ring-2 ring-white/10"
                        onError={(e) => { e.currentTarget.src = '/user.png'; }}
                      />
                      <Dot className="bg-green-500 top-0 -left-1" />
                      <span className="absolute -right-1 bottom-0 w-4 h-4 rounded-full overflow-hidden ring-2 ring-black/40">
                        <img src="/club.png" alt="Org" className="w-full h-full object-cover" />
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{remembered.name}</div>
                      <div className="text-xs text-white/60">{remembered.id}</div>
                    </div>
                    <button
                      type="button"
                      onClick={switchToAnotherUser}
                      className="ml-auto text-xs text-blue-300 hover:text-blue-200 underline underline-offset-2"
                    >
                      Sign in as another user
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm mb-1 text-gray-300">SLIIT ID</label>
                    <input
                      type="text"
                      className="w-full bg-[#121826] text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="ITXXXXXXXX"
                      value={id}
                      onChange={(e) => setId(e.target.value)}
                      autoComplete="username"
                    />
                  </div>
                )}

                {/* Password with refined eye toggle */}
<div>
  <label className="block text-sm mb-1 text-gray-300">Password</label>
  <div className="relative">
    <input
      type={showPw ? 'text' : 'password'}
      className="w-full bg-[#121826] text-white px-4 py-2 pr-12 rounded-lg 
                 focus:outline-none focus:ring-2 focus:ring-blue-500 
                 placeholder-gray-500"
      placeholder="••••••••"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      autoComplete="current-password"
    />

    {/* Eye toggle */}
    <button
      type="button"
      onClick={() => setShowPw((s) => !s)}
      aria-label={showPw ? 'Hide password' : 'Show password'}
      style={{
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-60%)", // 👈 lifts it higher
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "15px",
        width: "15px",
        borderRadius: "50%",
        background: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.2)",
        transition: "all 0.2s ease"
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.6}
        stroke="white"
        style={{ height: "16px", width: "16px" }}
      >
        {showPw ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 3l18 18M9.88 9.88A3 3 0 0112 9c1.66 0 3 1.34 3 3a3 3 0 01-.88 2.12M15.88 15.88A9.959 9.959 0 0112 17c-5 0-9.27-3-11-7 1.09-2.45 2.97-4.47 5.34-5.73M9.88 9.88L3 3"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M1.5 12s3.5-7 10.5-7 10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12zM12 15a3 3 0 100-6 3 3 0 000 6z"
          />
        )}
      </svg>

      {/* Tiny white dot (inline centered) */}
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: "2px",
          width: "2px",
          borderRadius: "20%",
          backgroundColor: "white",
          opacity: showPw ? 1 : 0.4,
          transition: "opacity 0.2s ease"
        }}
      />
    </button>
  </div>
</div>


                {error && <p className="text-red-400 text-sm">{error}</p>}

                <div className="flex items-center justify-between text-sm text-gray-400">
                  <span />
                  <a href="/forgot-password" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">
                    Forgot your password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 rounded-lg font-semibold transition
                             bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500
                             hover:from-blue-500 hover:via-indigo-500 hover:to-cyan-400
                             disabled:opacity-60"
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              <p className="text-center text-xs mt-6 text-gray-500">SLIIT Leo කුලේ </p>
            </div>
          </motion.div>
        </div>

        {/* RIGHT extras (waves already full-screen) */}
        <div className="relative hidden lg:block">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 -left-16 w-[360px] h-[360px] rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
          <div className="absolute top-8 right-8 p-3 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <img src="/club.png" alt="Club" className="h-8 w-8 rounded-md" />
              <div className="leading-tight">
                <div className="text-xs text-white/60">Powered by</div>
                <div className="font-semibold">Leo Club of SLIIT</div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile info block */}
        <div className="lg:hidden px-6 pb-10 -mt-4">
          <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/5">
            <div className="p-5 text-sm text-white/70">
              <div className="flex items-center gap-2">
                <img src="/club.png" alt="Club" className="h-5 w-5" />
                <span>Experience the new Portal 4.0</span>
              </div>
              <div className="mt-3 text-white/60">
                Experience the next generation of Leo engagement with our upgraded Portal 4.0.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-2 right-4 text-xs text-gray-400 z-10">
        <img src="/club.png" alt="Leo Logo" className="inline h-4 mr-1" />
        Leo Club of SLIIT, Lions International, District 306 D6
      </div>
    </div>
  );
}
